import { sheetPointsNew, sheetPointsToInches } from "common/utils/nominalNumbers";
import { aabb2AddPoint, aabb2ByPoints, aabb2Grow } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { mat3Dot, mat3Mult, mat3Rotate, mat3Scale, mat3Translate } from "geom/mat3";
import type { Vec2 } from "geom/vec2";
import { SVGElementToCollisionWeight } from "./SVGElementToCollisionWeight";
import type { LoadSVGResult } from "./SVGLoader";
import { SVGLoader, SVGtoPDFTextMetricsProvider } from "./SVGLoader";

export interface AABB2Weighted extends AABB2 {
  readonly weight: number;
}

export class SVGBoundingBoxProvider {
  private static _instance: SVGBoundingBoxProvider;
  private svgLoader: SVGLoader;

  private constructor() {
    this.svgLoader = new SVGLoader(undefined, true, new SVGtoPDFTextMetricsProvider());
  }

  static get instance(): SVGBoundingBoxProvider {
    return this._instance || (this._instance = new SVGBoundingBoxProvider());
  }

  getAABB2sFromSVGString(jsxString: string, x = 0, y = 0, rotation = 0, scaleX = 1, scaleY = 1): AABB2Weighted[] {
    const bbs: AABB2Weighted[] = [];
    const data = this.svgLoader.load(jsxString);
    if (data) {
      getBoundingBoxes(data, x, y, rotation, bbs, scaleX, scaleY);
    }
    return bbs;
  }

  getAABB2FromSVGString(jsxString: string, x = 0, y = 0, rotation = 0, scaleX = 1, scaleY = 1): AABB2 | null {
    const pts: Vec2[] = [];
    const data = this.svgLoader.load(jsxString);
    if (data) {
      pointsFromSVGElements(data, x, y, rotation, pts, scaleX, scaleY);
    }
    if (!pts || pts.length === 0) {
      return null;
    }
    return aabb2ByPoints(pts);
  }
}

function growBB(bb: AABB2): AABB2 {
  const amount = sheetPointsToInches(sheetPointsNew(1));
  return aabb2Grow(bb, amount, amount);
}

function getBoundingBoxes(
  data: LoadSVGResult,
  x: number,
  y: number,
  rotation: number,
  bbs: AABB2Weighted[],
  scaleX = 1,
  scaleY = 1,
) {
  const paths = data.paths;
  const geomTypes = data.geomTypes;
  const mat = makeTransformMatrix(scaleX, scaleY, rotation, x, y);

  if (!paths || !geomTypes) {
    return;
  }

  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];
    const geomType = geomTypes[i];

    const shapes = path.toShapes(true);
    if (!shapes) {
      continue;
    }

    for (let j = 0; j < shapes.length; j++) {
      const shape = shapes[j];
      const pts = shape.getPoints();

      if (!pts) {
        continue;
      }
      const { x: px, y: py } = pts[0];
      const [sx, sy] = mat3Dot(mat, px, py);
      const bb = aabb2New(sx, sy, sx, sy);

      if (pts.length === 1) {
        bbs.push({
          ...growBB(bb),
          weight: SVGElementToCollisionWeight[geomType] ?? SVGElementToCollisionWeight["default"],
        });
        continue;
      }

      for (let i = 1, l = pts.length; i < l; i += 1) {
        const { x: nx, y: ny } = pts[i];
        const [tx, ty] = mat3Dot(mat, nx, ny);
        aabb2AddPoint(bb, tx, ty);
      }

      bbs.push({
        ...growBB(bb),
        weight: SVGElementToCollisionWeight[geomType] ?? SVGElementToCollisionWeight["default"],
      });
    }
  }
}

function pointsFromSVGElements(
  data: LoadSVGResult,
  x: number,
  y: number,
  rotation: number,
  ptList: Vec2[],
  scaleX = 1,
  scaleY = 1,
) {
  const paths = data.paths;
  const geomTypes = data.geomTypes;

  const mat = makeTransformMatrix(scaleX, scaleY, rotation, x, y);

  if (!paths || !geomTypes) {
    return;
  }

  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];

    const shapes = path.toShapes(true);
    if (!shapes) {
      continue;
    }

    for (let j = 0; j < shapes.length; j++) {
      const shape = shapes[j];
      const pts = shape.getPoints();

      if (!pts) {
        continue;
      }

      for (let i = 0, l = pts.length; i < l; i += 1) {
        const { x: px, y: py } = pts[i];
        const [tx, ty] = mat3Dot(mat, px, py);
        ptList.push([tx, ty]);
      }
    }
  }
}

function makeTransformMatrix(scaleX: number, scaleY: number, rotation: number, x: number, y: number) {
  let mat = mat3Scale(scaleX, scaleY);
  mat = mat3Mult(mat3Rotate(rotation), mat);
  mat = mat3Mult(mat3Translate(x, y), mat);
  return mat;
}
